import Vue from 'vue'
import VueRouter from 'vue-router'
import { pick, truncate } from 'lodash'

import store from './store'

import ManageArtistIdentityRoutes from './router/manage/artist-identity.js'

import Wrapper from './layouts/default.vue'
import MessageOverlay from './components/MessageOverlay.vue'
import Accounts from './pages/accounts.vue'
import AccountsLogin from './pages/accounts/login.vue'
import AccountsRegister from './pages/accounts/register.vue'
import AccountsResetPassword from './pages/accounts/reset-password.vue'
import AccountsResetPasswordConfirm from './pages/accounts/reset-password-confirm.vue'
import AccountsLogout from './pages/accounts/logout.vue'
import Cart from './pages/cart.vue'
import DesignStudio from './pages/design-studio.vue'
import Search from './pages/search.vue'
import { getFullPathExcludingDisplayParams } from './pages/manage.vue'
import { supportsViewTransition, prefersReducedMotion } from './utils/viewTransition.js'
import { paramsSerializer } from './services/http'
import VAlert from './features/VAlert'
import { SKIN, SITE_TITLE } from './services/constants.js'

Vue.use(VueRouter)

const routes = [
  { path: '',
    component: Wrapper,
    meta: { title: SITE_TITLE },
    children: [
      {
        path: '/',
        name: 'index',
        component: () => {
          switch (SKIN) {
            case 'gov':   return import(/* webpackChunkName: 'gov' */ './pages/index-gov.vue')
            default:      return import(/* webpackChunkName: 'gaa' */ './pages/index.vue')
          }
        },
      },
      {
        path: '/accounts',
        redirect: '/accounts/login',
        component: Accounts,
        children: [
          {
            path: 'login',
            name: 'accounts-login',
            component: AccountsLogin,
            props: route => ({...route.params, ...route.query}),
            meta: { title: 'Sign in - Great American Art' },
          },
          {
            path: 'register',
            name: 'accounts-register',
            component: AccountsRegister,
            props: route => ({...route.params, ...route.query}),
            meta: { title: 'Sign up - Great American Art' },
          },
          {
            path: 'reset-password',
            name: 'accounts-reset-password',
            component: AccountsResetPassword,
            props: route => ({...route.params, ...route.query}),
            meta: { title: 'Reset Password - Great American Art' },
          },
          {
            path: 'reset-password-confirm/:uid?/:token?',
            name: 'accounts-reset-password-confirm',
            component: AccountsResetPasswordConfirm,
            props: route => ({...route.params, ...route.query}),
            meta: { title: 'Set Password - Great American Art' },
          },
        ],
      },
      {
        path: '/accounts/logout',
        name: 'accounts-logout',
        component: AccountsLogout,
        props: route => ({...route.params, ...route.query}),
      },
      { path: '/artists', redirect: '/artist' },
      {
        path: '/artist',
        component: {render(h) { return h('router-view') }},
        meta: { title: 'Artist Portal - Great American Art' },
        children: [
          {
            path: 'apply',
            component: () => import(/* webpackChunkName: 'artist' */ './pages/artist/apply.vue'),
            meta: { title: 'Apply to the Artist Portal - Great American Art' },
            children: [
              {
                path: '',
                name: 'artist-apply',
                component: () => import(/*webpackChunkName: 'artist' */ './pages/artist/apply/index.vue'),
              },
              {
                path: 'login',
                name: 'artist-login',
                component: () => import(/*webpackChunkName: 'artist' */ './pages/artist/apply/login.vue'),
              },
              {
                path: 'info',
                name: 'artist-info',
                component: () => import(/*webpackChunkName: 'artist' */ './pages/artist/apply/info.vue'),
                meta: { auth: true },
              },
              {
                path: 'sample',
                name: 'artist-sample',
                component: () => import(/*webpackChunkName: 'artist' */ './pages/artist/apply/sample.vue'),
                meta: { auth: true },
              },
              {
                path: 'applied',
                name: 'artist-applied',
                component: () => import(/*webpackChunkName: 'artist' */ './pages/artist/apply/applied.vue'),
                meta: { auth: true },
              },
            ],
          },
          {
            path: 'message',
            name: 'artist-message',
            component: () => import(/*webpackChunkName: 'artist' */ './pages/artist/message.vue'),
          },
          {
            path: 'new',
            component: () => import(/*webpackChunkName: 'artist' */ './pages/artist/new.vue'),
            meta: { auth: true, title: 'New Artist - Great American Art' },
            children: [
              {
                path: '',
                name: 'artist-new',
                component: () => import(/*webpackChunkName: 'artist' */ './pages/artist/new/index.vue'),
              },
              {
                path: 'royalty',
                name: 'artist-royalty',
                component: () => import(/*webpackChunkName: 'artist' */ './pages/artist/new/royalty.vue'),
              },
              {
                path: 'tax',
                name: 'artist-tax',
                component: () => import(/*webpackChunkName: 'artist' */ './pages/artist/new/tax.vue'),
              },
              {
                path: 'payment',
                name: 'artist-payment',
                component: () => import(/*webpackChunkName: 'artist' */ './pages/artist/new/payment.vue'),
                props: route => ({...route.params, ...route.query}),
              },
            ],
          },
          {
            path: '',
            component: () => import(/*webpackChunkName: 'artist' */ './pages/artist.vue'),
            children: [
              {
                path: '',
                name: 'artist',
                component: () => import(/*webpackChunkName: 'artist' */ './pages/artist/index.vue'),
              },
              {
                path: 'art',
                component: {render(h) { return h('router-view') }},
                meta: { auth: true },
                children: [
                  {
                    path: '',
                    name: 'artist-art',
                    component: () => import(/*webpackChunkName: 'artist' */ './pages/artist/art/index.vue'),
                    props: route => ({...route.params, ...route.query}),
                  },
                  {
                    path: 'all',
                    name: 'artist-art-all',
                    component: () => import(/*webpackChunkName: 'artist' */ './pages/manage/image/-all.vue'),
                    props: route => ({
                      royalty_code: store.state.user.artist?.royalty_code,
                      ...route.params,
                      ...route.query,
                    }),
                  },
                  {
                    path: ':id',
                    name: 'artist-art-id',
                    component: () => import(/*webpackChunkName: 'artist' */ './pages/artist/art/_id.vue'),
                    props: route => ({...route.params, ...route.query}),
                  },
                ],
              },
              {
                path: 'profile',
                name: 'artist-profile',
                component: () => import(/*webpackChunkName: 'artist' */ './pages/artist/profile/index.vue'),
                meta: { auth: true },
              },
              {
                path: 'profile/edit',
                name: 'artist-profile-edit',
                component: () => import(/*webpackChunkName: 'artist' */ './pages/artist/profile/edit.vue'),
                meta: { auth: true },
              },
              {
                path: 'upload/:advanced?',
                name: 'artist-upload',
                component: () => import(/*webpackChunkName: 'artist' */ './pages/artist/upload.vue'),
                meta: { auth: true },
                props: true,
              },
              {
                path: 'account',
                component: () => import(/*webpackChunkName: 'artist' */ './pages/artist/account.vue'),
                meta: { auth: true },
                children: [
                  {
                    path: '',
                    name: 'artist-account',
                    component: () => import(/*webpackChunkName: 'artist' */ './pages/artist/account/index.vue'),
                  },
                  {
                    path: 'royalty',
                    name: 'artist-account-royalty',
                    component: () => import(/*webpackChunkName: 'artist' */ './pages/artist/account/royalty.vue'),
                  },
                  {
                    path: 'tax',
                    name: 'artist-account-tax',
                    component: () => import(/*webpackChunkName: 'artist' */ './pages/artist/account/tax.vue'),
                  },
                  {
                    path: 'payment',
                    name: 'artist-account-payment',
                    component: () => import(/*webpackChunkName: 'artist' */ './pages/artist/account/payment.vue'),
                    props: route => ({...route.params, ...route.query}),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: '/:type(projects)',
        component: {render(h) { return h('router-view') }},
        children: [
          {
            path: '',
            name: 'projects',
            component: require('./pages/projects/index.vue').default,
            props: true,
            meta: { title: 'Projects - Great American Art' },
          },
          {
            path: ':id',
            name: 'projects-detail',
            component: require('./pages/projects/_id/index.vue').default,
            props: true,
          },
          {
            path: ':id/checkout',
            name: 'projects-detail-checkout',
            component: () => import(/* webpackChunkName: 'checkout' */ './pages/projects/_id/checkout.vue'),
            props: true,
            meta: { title: 'Checkout - Great American Art' },
          }
        ],
      },
      {
        path: '/:type(projects2)',
        component: {render(h) { return h('router-view') }},
        children: [
          {
            path: '',
            name: 'projects2',
            component: () => import('./pages/projects2/index.vue'),
            props: true,
            meta: { title: 'Projects 2 - Great American Art' },
          },
          {
            path: ':id',
            name: 'projects2-detail',
            component: () => import('./pages/projects2/_id/index.vue'),
            props: true,
            meta: {
              title: 'Project - Great American Art',
              transitionScope: 'design-studio-item',
            },
          },
        ],
      },
      {
        path: '/:type(orders)',
        component: {render(h) { return h('router-view') }},
        children: [
          {
            path: '',
            name: 'orders',
            component: () => import(/* webpackChunkName: 'checkout' */ './pages/orders/index.vue'),
            props: route => ({
              ...route.params,
              getName: item => `Order #${item.order_id}`,
            }),
            meta: { title: 'Orders - Great American Art' },
          },
          {
            path: ':id',
            name: 'orders-detail',
            component: () => import(/* webpackChunkName: 'checkout' */ './pages/orders/_id/index.vue'),
            props: route => ({
              ...route.params,
              getName: item => `Order #${item.order_id}`,
            }),
            meta: { title: 'Order - Great American Art' },
          },
        ],
      },
      {
        path: '/cart',
        name: 'cart',
        component: Cart,
        meta: { title: 'Cart - Great American Art' },
      },
      {
        path: '/checkout',
        name: 'checkout',
        component: () => import(/* webpackChunkName: 'checkout' */ './pages/checkout.vue'),
        meta: { title: 'Checkout - Great American Art' },
      },
      {
        path: '/checkout/confirmation/:id?',
        name: 'checkout-confirmation',
        component: () => import(/*webpackChunkName: 'checkout' */ './pages/checkout/confirmation.vue'),
        props: true,
        meta: { title: 'Order Confirmation - Great American Art' },
      },
      {
        path: '/manage/royalty-levels',
        name: 'manage-artist-royalty-levels',
        component: () => import(/*webpackChunkName: 'artist' */ './pages/manage/royalty-levels.vue'),
        meta: { auth: 'staff', title: 'Artist Royalty Levels - Great American Art' },
      },
      {
        path: '/manage/image',
        name: 'manage-type-image',
        component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/image/index.vue'),
        props: route => ({...route.query}),
        meta: { auth: 'staff', title: 'Images - Great American Art' },
      },
      {
        path: '/manage/image/:id',
        name: 'manage-image',
        component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/image/_id/index.vue'),
        props: route => ({...route.params, ...route.query}),
        meta: { auth: 'staff', title: 'Image - Great American Art' },
      },
      {
        path: '/manage/image/:id/edit',
        name: 'manage-image-edit',
        component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/image/_id/edit.vue'),
        props: route => ({...route.params, ...route.query}),
        meta: { auth: 'staff', title: 'Edit Image - Great American Art' },
      },
      {
        path: '/manage/:type(upload)/new',
        name: 'manage-upload-new',
        component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/upload/new/index.vue'),
        meta: { auth: 'staff', title: 'Upload - Great American Art' },
      },
      {
        path: '/manage/tax-reporting/:id?',
        name: 'manage-tax-reporting',
        component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/artist-identity/tax.vue'),
        props: route => ({...route.params, ...route.query}),
        meta: { auth: 'staff', title: 'Tax Reporting - Great American Art' },
      },
      {
        path: '/manage',
        component: () => import(/* webpackChunkName: 'manage' */ './pages/manage.vue'),
        meta: { auth: 'staff', title: 'Manage - Great American Art' },
        children: [
          {
            path: '',
            name: 'manage',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/index.vue'),
          },
          {
            path: ':type(art-usage)',
            name: 'manage-type-art-usage',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/art-usage/index.vue'),
            props: route => ({...route.params, ...route.query}),
          },
          {
            path: ':type(artist-profile)',
            name: 'manage-type-artist-profile',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/artist-profile/index.vue'),
            props: route => ({
              ...route.params,
              ...route.query,
              fields: [
                {key: 'full_name', sortable: true},
                {key: 'bio', autoshow: 'lg', formatter: i => truncate(i, {length: 30})},
                {key: 'email', autoshow: 'xl'},
                {key: 'approved', sortable: true, autoshow: 'sm', formatter: i => i ? '✓' : i===false ? '✗' : ''},
                {key: 'created', sortable: true, autoshow: 'sm'},
                {key: 'photo', autoshow: false, tdClass: 'column-narrow py-0 align-middle text-center'},
                {key: 'location', autoshow: false, formatter: i => i?.formatted_address ?? i}
              ],
            }),
          },
          {
            path: ':type(artist-profile)/email',
            name: 'manage-type-artist-profile-email',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/artist-profile/email.vue'),
            props: route => ({...route.params, ...route.query}),
          },
          {
            path: ':type(artist)',
            name: 'manage-type-artist',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/_type/index.vue'),
            props: route => ({
              ...route.params,
              ...route.query,
              fields: [
                {key: 'name', sortable: true},
                {key: 'gaa_artist', label: 'Portal', sortable: true, autoshow: 'sm', formatter: v => v ? '✓' : ''},
                {key: 'created', sortable: true, autoshow: 'sm'},
              ],
              tbodyTrClass: item => ({'text-muted': !item?.active}),
            }),
          },
          {
            path: ':type(collections)',
            name: 'manage-type-collections',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/_type/index.vue'),
            props: route => ({
              ...route.params,
              ...route.query,
              fields: [
                {key: 'name', sortable: true},
                {key: 'contents', label: '🖼️', thAttr: {title: 'Items'}, class: 'd-none d-sm-table-cell', formatter: v => v?.length.toLocaleString()},
                {key: 'owner', label: '👤', thAttr: {title: 'Owner'}, sortable: true, class: 'd-none d-sm-table-cell', formatter: v => v === store.state.user.user?.uuid ? '✓' : ''},
                {key: 'communal', label: '👥', thAttr: {title: 'Communal'}, sortable: true, class: 'd-none d-sm-table-cell', formatter: v => v ? '✓' : ''},
                {key: 'public', label: '🌐', thAttr: {title: 'Public'}, sortable: true, class: 'd-none d-sm-table-cell', formatter: v => v ? '✓' : ''},
                {key: 'created', sortable: true, class: 'd-none d-sm-table-cell'},
              ],
            }),
          },
          {
            path: ':type(upload)',
            name: 'manage-type-upload',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/upload/index.vue'),
            props: route => ({
              ...route.params,
              ...route.query,
              fields: [
                {key: 'name', sortable: true},
                {key: 'approved', sortable: true, autoshow: 'sm', formatter: i => i ? '✓' : i===false ? '✗' : ''},
                {key: 'published', sortable: true, autoshow: 'sm', formatter: v => v ? '✓' : ''},
                {key: 'created', sortable: true, autoshow: 'sm'},
              ],
            }),
          },
          {
            path: ':type(artist-identity)',
            name: 'manage-type-artist-identity',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/artist-identity/index.vue'),
            props: route => ({
              ...route.params,
              ...route.query,
              fields: [
                {key: 'name', sortable: false, formatter: (val, key, i) => i.businessName ? i.businessName : `${i.firstName} ${i.lastName}`},
                {key: 'email', sortable: false, autoshow: 'sm'},
                {key: 'status', sortable: true, autoshow: 'sm'},
                {key: 'createdOn', sortable: true, autoshow: 'sm'},
              ],
            }),
          },
          {
            path: ':type(artist-payment)',
            name: 'manage-type-artist-payment',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/artist-payment/index.vue'),
            props: route => ({...route.params, ...route.query}),
          },
          {
            path: 'payment-due',
            name: 'manage-type-payment-due',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/payment-due/index.vue'),
            props: route => ({...route.params, ...route.query}),
            meta: { title: 'Payments Due - Great American Art' },
          },
          {
            path: 'design-studio-projects',
            name: 'manage-design-studio-projects',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/design-studio-projects/index.vue'),
            props: route => ({...route.params, ...route.query}),
          },
          {
            path: 'design-studio-projects/:id',
            name: 'manage-design-studio-project',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/design-studio-projects/_id/index.vue'),
            props: true,
          },
          {
            path: 'design-studio-projects/:id/render',
            name: 'manage-design-studio-project-render',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/design-studio-projects/_id/render.vue'),
            props: true,
          },
          {
            path: ':type',
            name: 'manage-type',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/_type/index.vue'),
            props: route => ({...route.params, ...route.query}),
          },
          {
            path: ':type(artist-profile)/new',
            name: 'manage-artist-profile-new',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/artist-profile/_id/edit.vue'),
            props: true,
          },
          {
            path: ':type(artist-identity)/new',
            name: 'manage-artist-identity-new',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/artist-identity/_id/edit.vue'),
            props: true,
          },
          {
            path: ':type(artist)/new',
            name: 'manage-artist-new',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/artist/_id/edit.vue'),
            props: true,
          },
          {
            path: ':type(collections)/new',
            name: 'manage-collections-new',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/collections/_id/edit.vue'),
            props: true,
          },
          {
            path: ':type/new',
            name: 'manage-item-new',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/_type/_id/edit.vue'),
            props: true,
          },
          {
            path: ':type(art-usage)/:id',
            name: 'manage-art-usage',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/art-usage/_id/index.vue'),
            props: true,
          },
          {
            path: ':type(artist-profile)/:id',
            name: 'manage-artist-profile',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/artist-profile/_id/index.vue'),
            props: true,
          },
          {
            path: ':type(artist-identity)/:id',
            name: 'manage-artist-identity',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/artist-identity/_id/index.vue'),
            props: route => ({
              ...route.params,
              nameKey: 'email',
            }),
          },
          {
            path: ':type(artist-payment)/:id',
            name: 'manage-artist-payment',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/artist-payment/_id/index.vue'),
            props: true,
          },
          {
            path: ':type(artist-payment)/:id/status-transitions',
            name: 'manage-type-payment-status-transition',
            component: () => import(/*webpackChunkName: 'manage' */ '@/pages/manage/artist-payment/_id/status-transitions/index.vue'),
            props: route => ({
              ...route.params,
              ...route.query,
              nameKey: 'token',
            }),
          },
          {
            path: ':type(artist-payment)/:id/status-transitions/:stsToken',
            name: 'manage-payment-status-transition',
            component: () => import(/*webpackChunkName: 'manage' */ '@/pages/manage/artist-payment/_id/status-transitions/_stsToken.vue'),
            props: route => ({
              ...route.params,
              nameKey: 'token',
            }),
          },
          {
            path: ':type(artist)/:id',
            name: 'manage-artist',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/artist/_id/index.vue'),
            props: true,
          },
          {
            path: ':type(collections)/:id',
            name: 'manage-collections',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/collections/_id/index.vue'),
            props: true,
          },
          {
            path: ':type(publisher)/:id',
            name: 'manage-publisher',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/publisher/_id/index.vue'),
            props: true,
          },
          {
            path: ':type(upload)/:id',
            name: 'manage-upload',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/upload/_id/index.vue'),
            props: true,
          },
          {
            path: ':type/:id',
            name: 'manage-item',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/_type/_id/index.vue'),
            props: true,
          },
          {
            path: ':type(artist-profile)/:id/edit',
            name: 'manage-artist-profile-edit',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/artist-profile/_id/edit.vue'),
            props: true,
          },
          {
            path: ':type(artist-identity)/:id/edit',
            name: 'manage-artist-identity-edit',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/artist-identity/_id/edit.vue'),
            props: true,
          },
          {
            path: ':type(artist)/:id/edit',
            name: 'manage-artist-edit',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/artist/_id/edit.vue'),
            props: true,
          },
          {
            path: ':type(collections)/:id/edit',
            name: 'manage-collections-edit',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/collections/_id/edit.vue'),
            props: true,
          },
          {
            path: ':type/:id/edit',
            name: 'manage-item-edit',
            component: () => import(/* webpackChunkName: 'manage' */ './pages/manage/_type/_id/edit.vue'),
            props: true,
          },

          ...ManageArtistIdentityRoutes,
        ],
      },
      {
        path: '/design-studio',
        name: 'design-studio',
        component: DesignStudio,
        meta: {
          title: 'Design Studio - Great American Art',
          transitionScope: 'design-studio-item',
        },
      },
      { path: '/search',
        name: 'search',
        component: Search,
        props: route => Object.assign({}, route.params, route.query),
        meta: { title: route => (route.query.q ? route.query.q + ' - ' : '') + 'Great American Art Search' },
      },
      { path: '/images/:id',
        name: 'image-detail',
        component: require('./pages/images/_id.vue').default,
        props: route => ({...route.params, ...route.query}),
      },
      {
        path: '/:type(collections)/new',
        name: 'collections-new',
        component: () => import('./pages/collections/new.vue'),
        props: true,
        meta: { title: 'New Collection - Great American Art' },
      },
      {
        path: '/:type(collections)',
        component: {render(h) { return h('router-view') }},
        children: [
          {
            path: '',
            name: 'collections',
            component: () => import('./pages/collections/index.vue'),
            props: route => ({...route.params, ...route.query}),
            meta: { title: 'Collections - Great American Art' },
          },
          {
            path: ':id',
            component: () => import('./pages/collections/_id/index.vue'),
            props: true,
            children: [
              {
                path: '',
                name: 'collections-detail',
                component: () => import('./pages/collections/_id/-detail.vue'),
                props: route => ({...route.params, ...route.query}),
                meta: { title: 'Collection - Great American Art' },
              },
              {
                path: 'edit',
                name: 'collections-edit',
                component: () => import('./pages/collections/_id/-edit.vue'),
                props: route => ({...route.params, ...route.query}),
                meta: { title: 'Edit Collection - Great American Art' },
              },
            ],
          },
        ],
      },
      {
        path: '*',
        name: 'error',
        component: MessageOverlay,
        props: {
          messages: [{title: '404', description: 'Not Found'}],
        },
      },
    ]
  },
]

const scrollBehavior = function (to, from, savedPosition) {
  if (savedPosition) {
    return savedPosition
  }
  const urlChanged = getFullPathExcludingDisplayParams(to) !== getFullPathExcludingDisplayParams(from)
  if (urlChanged) {
    return { x: 0, y: 0 }
  }
}

const VIEW_TRANSITION_SETTLE_TIMEOUT_MS = 200

function waitForTransitionSettle() {
  return new Promise((resolve) => {
    let settled = false
    const done = () => {
      if (settled) return
      settled = true
      resolve()
    }

    setTimeout(done, VIEW_TRANSITION_SETTLE_TIMEOUT_MS)
    Vue.nextTick(() => {
      requestAnimationFrame(done)
    })
  })
}

const router = new VueRouter({
  mode: 'history',
  routes,
  scrollBehavior,
})

router.beforeEach((to, from, next) => {
  const fromScope = from.meta?.transitionScope
  const toScope = to.meta?.transitionScope
  const sharedScope = fromScope && toScope && fromScope === toScope
    ? fromScope
    : null
  const isScopedTransition = !!sharedScope

  if (isScopedTransition) {
    store.commit('appearance/setRouteTransition', sharedScope)
  }

  const cleanupTransitionState = () => {
    store.commit('appearance/setRouteTransition', null)
  }

  const shouldAnimate = supportsViewTransition()
    && !prefersReducedMotion()
    && (
      isScopedTransition
    )

  if (!shouldAnimate) {
    cleanupTransitionState()
    next()
    return
  }

  const transition = document.startViewTransition(() => {
    next()
    return waitForTransitionSettle()
  })

  if (transition?.finished?.finally) {
    transition.finished.finally(cleanupTransitionState)
  } else {
    cleanupTransitionState()
  }
})

router.afterEach((to, from) => {
  // clear errors on navigation
  const isFirstLoad = from.name === null
  if (!isFirstLoad) {
    store.dispatch('errors/clearError')
    VAlert.reset()
  }

  // make route info available for error handling
  const nearestWithAuth = to.matched.slice().reverse().find(r => r.meta && r.meta.auth)
  store.dispatch('errors/setRouteRequiresAuth', nearestWithAuth && pick(nearestWithAuth, ['name', 'path', 'meta']))
})

router.afterEach((to, from) => {
  // Set page title
  // https://github.com/vuejs/vue-router/issues/914#issuecomment-384477609

  // Preserve titles set by components.
  if (to.path === from.path && typeof to.meta?.title !== 'function') { return }

  // This goes through the matched routes from last to first, finding the closest route with a title.
  // eg. if we have /some/deep/nested/route and /some, /deep, and /nested have titles, nested's will be chosen.
  // https://alligator.io/vuejs/vue-router-modify-head/
  const nearestWithTitle = to.matched.slice().reverse().find(r => r.meta && r.meta.title)

  if (nearestWithTitle) {
    let title
    if (typeof nearestWithTitle.meta.title === 'function') {
      title = nearestWithTitle.meta.title(to)
    } else {
      title = nearestWithTitle.meta.title;
    }
    Vue.nextTick(() => {
      document.title = title
    })
  }
})

router.onError((err) => {
  store.dispatch('errors/setError', err)
  console.error(err) // throwing error would stop other onError callbacks from running, so use console.error
})

router.onReady(function () {
  // placed in onReady so it doesn't trigger on page load
  router.afterEach(analyticsGuard)
})

function analyticsGuard(to, from) {
  // Google Analyics track pageviews

  // whitelist query params for inclusion
  const allowedParams = [
    'q',
  ]

  // serialize url
  const [toUrl, fromUrl] = [to, from]
    .map(route => ({...route, query: pick(route.query, allowedParams)}))
    .map(route => {
      const qs = paramsSerializer(route.query).toString()
      return route.path + (qs ? '?'+qs : '')
    })

  // don't send pageview if locations match
  if (toUrl === fromUrl) { return }

  /* global dataLayer */
  dataLayer.push({
    event: 'pageview',
    page: {
      path: toUrl,
    },
  });
}

export default router
